"use client";

import { useState, useCallback, useMemo, useRef } from "react";
import type { MutableRefObject, RefObject } from "react";
import { CellKey, makeCellKey } from "./EditableTableContext";
import { ROW_HANDLE_WIDTH } from "./EditableTableRow";
import { ARROW_KEYS } from "./useTableKeyboard";

type CellPosition = { rowId: string; colIndex: number };
type SelectableColumn = { disableSelection?: boolean };

type UseTableSelectionProps<DataType> = {
  data: DataType[];
  getRowId: (row: DataType) => string;
  columns: SelectableColumn[];
  tableRef: RefObject<HTMLDivElement | null>;
  widthsRef: MutableRefObject<number[]>;
  pinnedTopRow?: { data: DataType };
};

export function useTableSelection<DataType>({
  data,
  getRowId,
  columns,
  tableRef,
  widthsRef,
  pinnedTopRow,
}: UseTableSelectionProps<DataType>) {
  const [selectedCells, setSelectedCells] = useState<Set<CellKey>>(new Set());
  const anchorCellRef = useRef<CellPosition | null>(null);
  const focusCellRef = useRef<CellPosition | null>(null);
  const scrollElRef = useRef<HTMLElement | null>(null);

  const selectableColIndices = useMemo(
    () =>
      columns.reduce<number[]>((acc, col, i) => (!col.disableSelection ? [...acc, i] : acc), []),
    [columns]
  );

  // Treat the pinned row as index 0 for keyboard navigation purposes.
  const allNavigableRows = useMemo(
    () => (pinnedTopRow ? [pinnedTopRow.data, ...data] : data),
    [pinnedTopRow, data]
  );

  const scrollColIntoView = useCallback(
    (colIndex: number) => {
      if (!tableRef.current) return;
      if (!scrollElRef.current?.isConnected) {
        let el: HTMLElement | null = tableRef.current.parentElement;
        while (el && el !== document.body) {
          const { overflowX, overflow } = getComputedStyle(el);
          if (["auto", "scroll"].includes(overflowX) || ["auto", "scroll"].includes(overflow))
            break;
          el = el.parentElement;
        }
        scrollElRef.current = el && el !== document.body ? el : null;
      }
      const scrollEl = scrollElRef.current;
      if (!scrollEl) return;
      const ws = widthsRef.current;
      const cellLeft = ws.slice(0, colIndex).reduce((a, w) => a + w, 0) + ROW_HANDLE_WIDTH;
      const cellRight = cellLeft + (ws[colIndex] ?? 0);
      const { scrollLeft, clientWidth } = scrollEl;
      if (cellRight > scrollLeft + clientWidth) {
        scrollEl.scrollLeft = cellRight - clientWidth;
      } else if (cellLeft < scrollLeft) {
        scrollEl.scrollLeft = cellLeft;
      }
    },
    [tableRef, widthsRef]
  );

  const moveToCell = useCallback(
    (rowId: string, colIndex: number) => {
      anchorCellRef.current = { rowId, colIndex };
      focusCellRef.current = { rowId, colIndex };
      setSelectedCells(new Set([makeCellKey(rowId, colIndex)]));
      scrollColIntoView(colIndex);
    },
    [scrollColIntoView]
  );

  // Like moveToCell but does not scroll or focus the table. Used when starting edit.
  const anchorToCell = useCallback((rowId: string, colIndex: number) => {
    anchorCellRef.current = { rowId, colIndex };
    focusCellRef.current = { rowId, colIndex };
    setSelectedCells(new Set([makeCellKey(rowId, colIndex)]));
  }, []);

  const selectCell = useCallback(
    (rowId: string, colIndex: number, metaKey: boolean, shiftKey?: boolean) => {
      if (shiftKey && anchorCellRef.current) {
        const anchor = anchorCellRef.current;
        const anchorRowIdx = data.findIndex(r => getRowId(r) === anchor.rowId),
          focusRowIdx = data.findIndex(r => getRowId(r) === rowId);
        if (anchorRowIdx !== -1 && focusRowIdx !== -1) {
          focusCellRef.current = { rowId, colIndex };
          const minRow = Math.min(anchorRowIdx, focusRowIdx),
            maxRow = Math.max(anchorRowIdx, focusRowIdx),
            minCol = Math.min(anchor.colIndex, colIndex),
            maxCol = Math.max(anchor.colIndex, colIndex);
          const cells = new Set<CellKey>();
          for (let r = minRow; r <= maxRow; r++) {
            for (let c = minCol; c <= maxCol; c++) {
              cells.add(makeCellKey(getRowId(data[r]), c));
            }
          }
          setSelectedCells(cells);
          tableRef.current?.focus({ preventScroll: true });
          return;
        }
      }

      const key = makeCellKey(rowId, colIndex);
      if (!metaKey) {
        anchorCellRef.current = { rowId, colIndex };
        focusCellRef.current = { rowId, colIndex };
      }
      setSelectedCells(prev => {
        if (metaKey) {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        }
        return new Set([key]);
      });
      tableRef.current?.focus({ preventScroll: true });
    },
    [data, getRowId, tableRef]
  );

  const clearSelection = useCallback(() => {
    setSelectedCells(prev => (prev.size === 0 ? prev : new Set()));
  }, []);

  const isCellSelected = useCallback(
    (rowId: string, colIndex: number) => selectedCells.has(makeCellKey(rowId, colIndex)),
    [selectedCells]
  );

  const advanceCell = useCallback(
    (fromRowId: string, fromColIndex: number, reverse = false) => {
      if (allNavigableRows.length === 0 || selectableColIndices.length === 0) return;
      const rowIdx = allNavigableRows.findIndex(r => getRowId(r) === fromRowId);
      if (rowIdx === -1) return;
      const colPos = selectableColIndices.indexOf(fromColIndex);
      if (colPos === -1) return;
      if (!reverse) {
        if (rowIdx + 1 < allNavigableRows.length) {
          moveToCell(getRowId(allNavigableRows[rowIdx + 1]), fromColIndex);
        } else {
          const nextColPos = colPos + 1 < selectableColIndices.length ? colPos + 1 : 0;
          moveToCell(getRowId(allNavigableRows[0]), selectableColIndices[nextColPos]);
        }
      } else {
        if (rowIdx > 0) {
          moveToCell(getRowId(allNavigableRows[rowIdx - 1]), fromColIndex);
        } else {
          const prevColPos = colPos > 0 ? colPos - 1 : selectableColIndices.length - 1;
          moveToCell(
            getRowId(allNavigableRows[allNavigableRows.length - 1]),
            selectableColIndices[prevColPos]
          );
        }
      }
    },
    [allNavigableRows, getRowId, selectableColIndices, moveToCell]
  );

  const tabCell = useCallback(
    (fromRowId: string, fromColIndex: number, reverse = false) => {
      if (allNavigableRows.length === 0 || selectableColIndices.length === 0) return;
      const rowIdx = allNavigableRows.findIndex(r => getRowId(r) === fromRowId);
      if (rowIdx === -1) return;
      const colPos = selectableColIndices.indexOf(fromColIndex);
      if (colPos === -1) return;

      if (!reverse) {
        if (colPos < selectableColIndices.length - 1) {
          moveToCell(fromRowId, selectableColIndices[colPos + 1]);
        } else {
          const nextRowIdx = rowIdx + 1 < allNavigableRows.length ? rowIdx + 1 : 0;
          moveToCell(getRowId(allNavigableRows[nextRowIdx]), selectableColIndices[0]);
        }
      } else {
        if (colPos > 0) {
          moveToCell(fromRowId, selectableColIndices[colPos - 1]);
        } else {
          const prevRowIdx = rowIdx > 0 ? rowIdx - 1 : allNavigableRows.length - 1;
          moveToCell(
            getRowId(allNavigableRows[prevRowIdx]),
            selectableColIndices[selectableColIndices.length - 1]
          );
        }
      }
    },
    [allNavigableRows, getRowId, selectableColIndices, moveToCell]
  );

  const expandSelection = useCallback(
    (dir: (typeof ARROW_KEYS)[number]) => {
      const anchor = anchorCellRef.current,
        focus = focusCellRef.current;
      if (!anchor || !focus) return;

      let newRowIdx = allNavigableRows.findIndex(r => getRowId(r) === focus.rowId),
        newColIdx = focus.colIndex;

      if (dir === "ArrowUp") newRowIdx = Math.max(0, newRowIdx - 1);
      else if (dir === "ArrowDown")
        newRowIdx = Math.min(allNavigableRows.length - 1, newRowIdx + 1);
      else if (dir === "ArrowLeft") newColIdx = Math.max(0, newColIdx - 1);
      else newColIdx = Math.min(columns.length - 1, newColIdx + 1);

      const newFocusRowId = getRowId(allNavigableRows[newRowIdx]);
      focusCellRef.current = { rowId: newFocusRowId, colIndex: newColIdx };

      const anchorRowIdx = allNavigableRows.findIndex(r => getRowId(r) === anchor.rowId),
        minRow = Math.min(anchorRowIdx, newRowIdx),
        maxRow = Math.max(anchorRowIdx, newRowIdx),
        minCol = Math.min(anchor.colIndex, newColIdx),
        maxCol = Math.max(anchor.colIndex, newColIdx);

      const cells = new Set<CellKey>();
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          cells.add(makeCellKey(getRowId(allNavigableRows[r]), c));
        }
      }
      setSelectedCells(cells);
    },
    [allNavigableRows, getRowId, columns.length]
  );

  const selectColumn = useCallback(
    (colIndex: number) => {
      const col = columns[colIndex];
      if (col?.disableSelection) return;
      const cells = new Set<CellKey>();
      for (const row of data) {
        cells.add(makeCellKey(getRowId(row), colIndex));
      }
      const alreadySelected = data.length > 0 && [...cells].every(k => selectedCells.has(k));
      if (alreadySelected) {
        setSelectedCells(new Set());
        anchorCellRef.current = null;
        focusCellRef.current = null;
      } else {
        setSelectedCells(cells);
        if (data.length > 0) {
          anchorCellRef.current = { rowId: getRowId(data[0]), colIndex };
          focusCellRef.current = { rowId: getRowId(data[data.length - 1]), colIndex };
        }
      }
      tableRef.current?.focus({ preventScroll: true });
    },
    [columns, data, getRowId, selectedCells, tableRef]
  );

  return {
    selectedCells,
    anchorCellRef,
    focusCellRef,
    selectCell,
    clearSelection,
    isCellSelected,
    expandSelection,
    selectColumn,
    advanceCell,
    tabCell,
    anchorToCell,
  };
}
